<template>
  <section
      id="dmr-section"
      class="dmr-section push-v--desktop--xx-large push-v--tablet--x-large push-v--mobile--x-large"
  >
    <!-- Toggle -->
    <div class="dmr-section__toggle-wrapper col-10 offset-1" data-entrance="fade">
      <button
          type="button"
          class="dmr-section__toggle"
          :class="{ 'is-open': isOpen }"
          :aria-expanded="isOpen ? 'true' : 'false'"
          aria-controls="dmr-section-panel"
          @click="isOpen = !isOpen"
      >
        <span>{{ toggleText }}</span>
        <svg class="dmr-section__toggle-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M3 6L8 11L13 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>

    <div v-show="isOpen" id="dmr-section-panel" class="dmr-section__panel">
    <!-- Split Block - Text Left, Image Right -->
    <div class="dmr-section__split-block">
      <div class="col-10 offset-1">
        <div class="dmr-section__split-inner">
          <div class="row align-items-center no-gutters">
            <!-- Text Content - Left Side -->
            <div class="col-12 col-lg-6 dmr-section__text-content">
              <div class="dmr-section__text-wrapper">
                <h2 class="dmr-section__heading h4">{{ heading }}</h2>
                <div class="dmr-section__copy" v-html="copy" />
              </div>
            </div>

            <!-- Image - Right Side -->
            <div class="col-12 col-lg-6 dmr-section__image-content">
              <div class="dmr-section__image-wrapper">
                <span class="dmr-section__image-caption">{{ imageCaption }}</span>
                <img
                    :src="imageUrl"
                    :alt="imageCaption"
                    class="dmr-section__image"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Three Column Eligibility Section -->
    <div class="dmr-section__eligibility push-v--desktop--x-large push-v--tablet--large push-v--mobile--large">
      <div class="col-10 offset-1 np--all">
        <div class="container">
          <!-- Title and Subtitle -->
          <div class="row justify-content-center">
            <div class="col-12 text-center push-v--desktop--medium push-v--tablet--small push-v--mobile--small">
              <h3 class="dmr-section__eligibility-title h5">{{ eligibilityTitle }}</h3>
              <p class="dmr-section__eligibility-subtitle">{{ eligibilitySubtitle }}</p>
            </div>
          </div>

          <!-- Three Columns -->
          <div class="row justify-content-center push-v--desktop--medium push-v--tablet--medium push-v--mobile--medium">
            <div
                v-for="(eligibility, index) in eligibilities"
                :key="index"
                class="col-12 col-md-4 dmr-section__eligibility-item"
            >
              <svg class="dmr-section__eligibility-icon" width="32" height="33" viewBox="0 0 32 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.66699 11.1064L15.643 4.61842C15.754 4.56302 15.8763 4.53418 16.0003 4.53418C16.1244 4.53418 16.2467 4.56302 16.3577 4.61842L29.3337 11.1064M26.667 15.1064V25.7731C26.667 26.4803 26.386 27.1586 25.8859 27.6587C25.3858 28.1588 24.7076 28.4398 24.0003 28.4398H8.00033C7.29308 28.4398 6.6148 28.1588 6.11471 27.6587C5.61461 27.1586 5.33366 26.4803 5.33366 25.7731V15.1064" stroke="#181631" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <p class="dmr-section__eligibility-text">{{ eligibility.text }}</p>
            </div>
          </div>

          <!-- Bottom Text -->
          <div class="row justify-content-center">
            <div class="col-12 text-center push-v--desktop--small push-v--tablet--small push-v--mobile--small">
              <p class="dmr-section__eligibility-footer">{{ eligibilityFooter }}</p>
            </div>
            <div class="col-12 text-center">
            <a :href="`mailto:${enquiryEmail}`" class="btn btn--dark-green">
              {{ enquiryButtonText }}
            </a>
          </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  </section>
</template>

<script>
export default {
  data() {
    return {
      isOpen: false,
    };
  },

  props: {
    toggleText: {
      type: String,
      default: 'Find out if you\'re eligible for a Discount Market Rent studio at The Collective Old Oak',
    },
    heading: {
      type: String,
      default: 'Discount Market Rent at The Collective Old Oak',
    },
    copy: {
      type: String,
      default: `At The Collective Old Oak, we believe that everyone should be able to experience more than just a place to live. Our Discount Market Rent (DMR) homes make it possible to enjoy all the perks of life at The Collective Old Oak at a reduced monthly rate.<br><br>As a DMR Member, you'll have access to all the same amenities as everyone else. That means co-working spaces, a gym, a cinema room, shared kitchens, and a packed events calendar designed to bring people together. Whether you're working, working out, or winding down, Old Oak has space for every part of your day.`,
    },
    imageUrl: {
      type: String,
      default: 'https://assets-us-01.kc-usercontent.com/9a170b3a-a06f-006a-7810-f1c91cdd7d0b/61eef9db-4d7e-44d4-b884-8acb968a6a48/Ensuite%209.png',
    },
    imageCaption: {
      type: String,
      default: 'Compact Two Bedroom Apartment',
    },
    eligibilityTitle: {
      type: String,
      default: 'Eligibility Criteria',
    },
    eligibilitySubtitle: {
      type: String,
      default: 'To be eligible, DMR Members need to meet certain income and location criteria. The homes are allocated across three income bands:',
    },
    eligibilities: {
      type: Array,
      default() {
        return [
          {
            text: 'House Income less than £42,600 per year',
          },
          {
            text: 'House Income less than £56,800 per year',
          },
          {
            text: 'House Income less than £71,000 per year',
          },
        ];
      },
    },
    eligibilityFooter: {
      type: String,
      default: 'First priority goes to people who live or work in the Borough of Ealing, and then those of Brent, Harrow and Hillingdon. To qualify, applicants must have lived in the Borough for at least 4 weeks prior to applying. All income limits are reviewed and updated in line with inflation, so they may change slightly over time.',
    },
    enquiryEmail: {
      type: String,
      default: 'oldoak@thecollective.com',
    },
    enquiryButtonText: {
      type: String,
      default: 'Enquire Now',
    },
  },
};
</script>

<style lang="scss">
@import '~/assets/scss/atomic/modules/_dmr-section.scss';
</style>
